import { request } from '@wix/da-shared-react/pkg/xhr';
import { API_BASE_PATH } from '../../constants';
import { select, call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import { selectors as streamSelectors } from '@wix/da-shared-react/pkg/Stream';
import { insertItems } from '@wix/da-shared-react/pkg/Stream/withOffset/actions';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { getShopItemsStreamId } from '../streams/shop';
import { ShopItemType } from '../../types/shop';
import { CommissionStatusCodeMap } from '@wix/da-shared-react/pkg/types/commissions';
import { getDeviationsByIds } from '@wix/da-shared-react/pkg/redux/deviations/selectors';
import { getModuleIdByModuleType } from '../selectors/modules';
import { updateModule } from '../actions/modules';
import { ModuleType } from '../../types/modules';
import {
  changeCommissionsGlobalStatus as changeCommissionsGlobalStatusAction,
  changeAllCommissionsStatus as changeAllCommissionsStatusAction,
  fetchCommissionsSuccess as fetchCommissionsSuccessAction,
} from '../actions/commissions';
import { createCommissionSuccess } from '@wix/da-shared-react/pkg/redux/commissions/actionCreators';
import {
  PapiRequestDaUserProfileModuleCommissionsChangeAllCommissionsStatus,
  PapiRequestDaUserProfileModuleCommissionsChangeGlobalStatus,
} from '@wix/da-types-papi';

function* changeAllCommissionsStatus(data) {
  return yield call(request, {
    method: 'post',
    baseURL: API_BASE_PATH,
    url: '/module/commissions/change_all_commissions_status',
    data,
  });
}

function* changeCommissionsGlobalStatus(data) {
  return yield call(request, {
    method: 'post',
    baseURL: API_BASE_PATH,
    url: '/module/commissions/change_global_status',
    data,
  });
}

export function* changeCommissionsGlobalStatusSaga(action) {
  const { isOpen } = action.payload;
  const params: PapiRequestDaUserProfileModuleCommissionsChangeGlobalStatus = {
    is_open: isOpen,
  };
  const response = yield call(changeCommissionsGlobalStatus, params);

  if (response && response.success) {
    const moduleId = yield select(
      getModuleIdByModuleType,
      ModuleType.COMMISSIONS
    );

    yield put(
      updateModule({
        id: moduleId,
        moduleData: { isOpenForCommissions: isOpen },
      })
    );
  }
}

export function* changeAllCommissionsStatusSaga(action) {
  const { status } = action.payload;
  const params: PapiRequestDaUserProfileModuleCommissionsChangeAllCommissionsStatus = {
    status,
  };
  const response = yield call(changeAllCommissionsStatus, params);

  if (response && response.success) {
    const streamId = getShopItemsStreamId(ShopItemType.COMMISSIONS);
    const commissionsIds = yield select(streamSelectors.getItems, streamId);
    const commissions = yield select(getDeviationsByIds, commissionsIds);
    const updatedCommissions = commissions.map(deviation => {
      deviation.commission.status =
        CommissionStatusCodeMap[response.data.status];
    });

    const normalizedDeviations = normalize(updatedCommissions, deviationSchema);
    yield put(fetchCommissionsSuccessAction(normalizedDeviations));
  }
}

export function* createCommissionSuccessSaga(action) {
  const responseDeviation = action.payload;
  const { result, entities } = normalize(responseDeviation, deviationSchema);
  const streamId = getShopItemsStreamId(ShopItemType.COMMISSIONS);
  const existingItems = yield select(streamSelectors.getItems, streamId) || [];
  const isFirstCommission = existingItems.length === 0;

  yield put(
    insertItems({
      streamId,
      items: [result],
      offset: 0,
      entities,
    })
  );

  // if this was the first ever commission, backend opens up the global setting
  if (isFirstCommission) {
    const moduleId = yield select(
      getModuleIdByModuleType,
      ModuleType.COMMISSIONS
    );

    yield put(
      updateModule({
        id: moduleId,
        moduleData: { isOpenForCommissions: true },
      })
    );
  }
}

export default [
  takeLatest(
    changeCommissionsGlobalStatusAction,
    changeCommissionsGlobalStatusSaga
  ),
  takeLatest(changeAllCommissionsStatusAction, changeAllCommissionsStatusSaga),
  takeEvery(createCommissionSuccess, createCommissionSuccessSaga),
];
